<template>
    <card-wrapper :title="Lang.get('employerdashboard::pages/employees/index.title') "
                  :subtitle="Lang.get('employerdashboard::pages/employees/index.subtitle')">

        <template v-slot:nav aria-label="breadcrumb">
            <ol class="breadcrumb font-poppins font-weight-light font-size-14 color-blue px-0 bg-transparent">
                <li class="breadcrumb-item color-blue" aria-current="page">
                    {{ Lang.get('employerdashboard::pages/employees/index.title') }}
                </li>
            </ol>
        </template>

        <div class="col-12 col-md-8">
            <flash-messages></flash-messages>
        </div>
        <card-layout :size="'large'" card_padding="px-0" class="flex-nowrap">
            <ul class="nav nav-tabs flex-nowrap px-3" id="employeeTab" role="tablist">
                <li class="nav-item">
                    <inertia-link class="nav-link font-poppins font-size-14 color-blue"
                                  :class="{active: this.tab === 'active'}"
                                  id="active-tab"
                                  data-toggle="tab"
                                  :href="route('nl.employerdashboard.employees.overview', $page.props.auth.user.selected_company.uuid)"
                                  role="tab"
                                  aria-controls="home"
                                  :aria-selected="{true: this.tab === 'active'}">
                        {{ Lang.get('employerdashboard::pages/employees/index.tabs.active') }} ({{amountOfActiveEmployees}})
                    </inertia-link>
                </li>
                <li class="nav-item">
                    <inertia-link class="nav-link font-poppins font-size-14 color-blue"
                                  :class="{active: this.tab === 'inactive'}"
                                  id="inactive-tab"
                                  data-toggle="tab"
                                  :href="route('nl.employerdashboard.employees.overview', {company_as_uuid: $page.props.auth.user.selected_company.uuid, tab: 'inactive'})"
                                  role="tab"
                                  aria-controls="inactive"
                                  :aria-selected="{true: this.tab === 'inactive'}">
                        {{ Lang.get('employerdashboard::pages/employees/index.tabs.inactive') }} ({{
                            amountOfInactiveEmployees
                        }})
                    </inertia-link>
                </li>
            </ul>
            <div class="tab-content" id="employeeTabContent">
                <div class="tab-pane fade show active" id="all" role="tabpanel" aria-labelledby="all-tab">
                    <div class="form-group input-group px-4 pt-4 m-0">
                        <input type="text" v-model="form.search"
                               class="form-control form-control-lg my-2 input-search font-poppins color-blue"
                               id="search"
                               :placeholder="Lang.get('employerdashboard::shared.search_an_employee')"
                               autocomplete="off">
                    </div>

                    <template v-if="employees.data.length === 0">
                        <div class="col-12 px-4 py-3">
                            <p class="font-poppins font-weight-light font-size-18 color-blue">
                                <!-- No search results -->
                                <span v-if="filters.search">
                                            {{ Lang.get('employerdashboard::pages/employees/index.search.no_results') }}
                                        </span>
                                <!-- No search executed, no employees found -->
                                <span v-else-if="tab === 'inactive'">
                                            {{ Lang.get('employerdashboard::pages/employees/index.no_inactive_employees') }}
                                        </span>

                                <span v-else-if="tab === 'active'">
                                            {{ Lang.get('employerdashboard::pages/employees/index.no_employees') }}
                                        </span>
                            </p>
                        </div>
                    </template>
                    <template v-else>
                        <div id="employeeAccordion">
                            <div class="card border-0" v-for="employee in employees.data" :key="employee.id">
                                <inertia-link
                                    :href="route('nl.employerdashboard.employee.profile', {'company_as_uuid': $page.props.auth.user.selected_company.uuid, 'employee_uuid': employee.uuid})"
                                    class="font-poppins font-weight-light font-size-18 color-blue no-underline border-bottom">
                                    <div class="card-header px-4 py-3 bg-white cursor-pointer collapsed">
                                        {{ employee.fullname }}
                                        <span v-if="employee.inactivated_at" class="badge badge-secondary font-poppins font-size-11 font-weight-semibold float-right mt-1">
                                            {{ Lang.get('employerdashboard::pages/employees/index.employee.statuses.inactive') }}
                                        </span>
                                        <span
                                            class="font-poppins font-size-11 font-weight-semibold float-right mt-1"
                                            v-if="!employee.has_email && !employee.is_disabled_from_participation && !$page.props.auth.isCompanyProviderManually">
                                            <i class="fa fa-exclamation-circle color-red"></i>
                                        </span>
                                    </div>
                                </inertia-link>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
            <pagination :links="employees.links"/>
        </card-layout>
        <div class="col-12 col-md-4 mb-4">
            <div class="row">
                <div class="col-12 mb-4" v-if="$page.props.auth.isCompanyProviderManually">
                    <inertia-link
                        class="btn btn-primary font-poppins font-weight-bold btn-orange border-radius-19 px-4"
                        :href="route('nl.employerdashboard.employee.add', this.$page.props.auth.user.selected_company.uuid)">
                        {{ Lang.get('employerdashboard::pages/employees/index.new_employee') }}
                    </inertia-link>
                </div>
                <div class="col-12 mb-4" v-if="$page.props.problems.problem_with_provider">
                    <div class="card h-100 border-radius-8 bg-error-message shadow"
                         v-for="problem in $page.props.problems.problem_with_provider">
                        <div class="card-body">
                            <h6 class="card-title font-poppins font-weight-extrabold color-white">
                                {{ problem.title }}
                            </h6>
                            <div class="card-text color-white font-size-14">{{ problem.description }}</div>
                        </div>
                        <div class="card-footer bg-error-message border-0 mb-1">
                            <inertia-link
                                class="btn btn-primary font-aileron-bold btn-white border-radius-19"
                                :href="problem.link">
                                {{ Lang.get('employerdashboard::pages/employees/index.provider.error.button_text') }}
                            </inertia-link>
                        </div>
                    </div>
                </div>
                <div class="col-12 mb-4">
                    <SpreadsheetSyncStatus
                        v-if="providerType === 'spreadsheet'"
                        :sync="sync"
                        :upload-feature-enabled="employeeImportUploadFeatureEnabled"
                        :company-uuid="company.uuid"
                        :import-missing-for-current-period="employeeImportIsMissingForCurrentPeriod"
                        :employeeImportInboxEmail="employeeImportInboxEmail"
                        :latest-successful-employee-import="latestSuccessfulEmployeeImport"
                        :is-authorized-from-scp="$page.props.auth.isAuthorizedFromScp"
                    />
                    <SyncStatus
                        v-else
                        :sync="sync" />
                </div>
            </div>
        </div>
    </card-wrapper>
</template>

<script>
import CardWrapper from '@/Shared/CardWrapper'
import CardLayout from "@/Shared/CardLayout"
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import pickBy from 'lodash/pickBy'
import SearchFilter from '@/Shared/Search'
import throttle from 'lodash/throttle'
import SyncStatus from '@/Shared/SyncStatus'
import FlashMessages from '@/Shared/FlashMessages'
import SpreadsheetSyncStatus from '@/Shared/SpreadsheetSyncStatus'

export default {
    metaInfo: { title: Lang.get('employerdashboard::pages/employees/index.title') },
    components: {
        SpreadsheetSyncStatus,
        CardWrapper,
        CardLayout,
        Pagination,
        SearchFilter,
        SyncStatus,
        FlashMessages,
    },
    props: {
        tab: String,
        company: Object,
        employees: Object,
        filters: Object,
        amountOfInactiveEmployees: Number,
        amountOfActiveEmployees: Number,
        sync: Object,
        providerType: String,
        employeeImportUploadFeatureEnabled: Boolean,
        employeeImportIsMissingForCurrentPeriod: Boolean,
        employeeImportInboxEmail: String,
        latestSuccessfulEmployeeImport: Object,
    },
    data() {
        return {
            form: {
                search: this.filters.search,
            },
        }
    },
    watch: {
        form: {
            handler: throttle(function() {
                let query = pickBy(this.form)

                this.$inertia.get(route('nl.employerdashboard.employees.overview', {company_as_uuid: this.company.uuid, tab: this.tab}), Object.keys(query).length ? query : {},{
                    preserveState: true,
                    preserveScroll: false,
                    replace: true,
                    only: [],
                })
            }, 150),
            deep: true,
        },
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
